import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiDollarSign, FiUser, FiCreditCard, FiCalendar, FiSearch, FiX, FiCheck, FiAlertCircle, FiRefreshCw, FiPause, FiPlay } from 'react-icons/fi';
import { supabase } from '../../lib/supabase';
import authorizeNetRecurring from '../../services/authorizeNetRecurring';

const SubscriptionManager = () => {
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [showCreateSubscription, setShowCreateSubscription] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [customerForm, setCustomerForm] = useState({
    name: '', email: '', phone: '', cardNumber: '', expirationDate: '', cvv: '',
    address: '', city: '', state: '', zip: ''
  });

  const [subscriptionForm, setSubscriptionForm] = useState({
    customerId: '', name: '', amount: '', startDate: '', intervalLength: '1', intervalUnit: 'months',
    totalPayments: '', description: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [custRes, subRes, payRes] = await Promise.all([
        supabase.from('customer_payment_profiles').select('*').order('created_at', { ascending: false }),
        supabase.from('payment_subscriptions').select('*, customer_payment_profiles(customer_name, customer_email)').order('created_at', { ascending: false }),
        supabase.from('payment_history').select('*, customer_payment_profiles(customer_name)').order('payment_date', { ascending: false }).limit(100)
      ]);
      setCustomers(custRes.data || []);
      setSubscriptions(subRes.data || []);
      setPayments(payRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setMessage({ type: '', text: '' });

    try {
      const nameParts = customerForm.name.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || '';

      const result = await authorizeNetRecurring.createCustomerProfile({
        customerId: `CUST-${Date.now()}`,
        email: customerForm.email,
        description: customerForm.name,
        paymentProfile: customerForm.cardNumber ? {
          firstName, lastName,
          cardNumber: customerForm.cardNumber,
          expirationDate: customerForm.expirationDate,
          cvv: customerForm.cvv,
          address: customerForm.address,
          city: customerForm.city,
          state: customerForm.state,
          zip: customerForm.zip,
          phone: customerForm.phone
        } : null
      });

      if (result.success) {
        const { data: profile } = await supabase.from('customer_payment_profiles').insert({
          customer_name: customerForm.name,
          customer_email: customerForm.email,
          customer_phone: customerForm.phone,
          authorize_customer_profile_id: result.customerProfileId
        }).select().single();

        if (customerForm.cardNumber && result.paymentProfileIds?.[0]) {
          await supabase.from('payment_methods').insert({
            customer_profile_id: profile.id,
            authorize_payment_profile_id: result.paymentProfileIds[0],
            card_type: getCardType(customerForm.cardNumber),
            card_last_four: customerForm.cardNumber.slice(-4),
            card_expiration: customerForm.expirationDate,
            billing_name: customerForm.name,
            billing_address: customerForm.address,
            billing_city: customerForm.city,
            billing_state: customerForm.state,
            billing_zip: customerForm.zip,
            is_default: true
          });
        }

        setMessage({ type: 'success', text: 'Customer created successfully!' });
        setShowCreateCustomer(false);
        resetCustomerForm();
        loadData();
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to create customer' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error creating customer' });
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateSubscription = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setMessage({ type: '', text: '' });

    try {
      const customer = customers.find(c => c.id === subscriptionForm.customerId);
      if (!customer) throw new Error('Customer not found');

      const { data: methods } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('customer_profile_id', customer.id)
        .eq('is_default', true)
        .single();

      if (!methods) {
        setMessage({ type: 'error', text: 'Customer has no payment method on file' });
        setProcessing(false);
        return;
      }

      const result = await authorizeNetRecurring.createSubscriptionFromProfile({
        name: subscriptionForm.name,
        amount: parseFloat(subscriptionForm.amount),
        startDate: subscriptionForm.startDate,
        intervalLength: parseInt(subscriptionForm.intervalLength),
        intervalUnit: subscriptionForm.intervalUnit,
        totalOccurrences: subscriptionForm.totalPayments ? parseInt(subscriptionForm.totalPayments) : 9999,
        customerProfileId: customer.authorize_customer_profile_id,
        paymentProfileId: methods.authorize_payment_profile_id
      });

      if (result.success) {
        await supabase.from('payment_subscriptions').insert({
          customer_profile_id: customer.id,
          payment_method_id: methods.id,
          authorize_subscription_id: result.subscriptionId,
          name: subscriptionForm.name,
          description: subscriptionForm.description,
          amount: parseFloat(subscriptionForm.amount),
          interval_length: parseInt(subscriptionForm.intervalLength),
          interval_unit: subscriptionForm.intervalUnit,
          start_date: subscriptionForm.startDate,
          total_payments: subscriptionForm.totalPayments ? parseInt(subscriptionForm.totalPayments) : null,
          next_payment_date: subscriptionForm.startDate,
          status: 'active'
        });

        setMessage({ type: 'success', text: 'Subscription created successfully!' });
        setShowCreateSubscription(false);
        resetSubscriptionForm();
        loadData();
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to create subscription' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Error creating subscription' });
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async (sub) => {
    if (!confirm(`Cancel subscription "${sub.name}"? This will stop all future payments.`)) return;
    setProcessing(true);

    try {
      if (sub.authorize_subscription_id) {
        await authorizeNetRecurring.cancelSubscription(sub.authorize_subscription_id);
      }
      await supabase.from('payment_subscriptions')
        .update({ status: 'canceled', canceled_at: new Date().toISOString() })
        .eq('id', sub.id);
      setMessage({ type: 'success', text: 'Subscription canceled' });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error canceling subscription' });
    } finally {
      setProcessing(false);
    }
  };

  const handleChargeNow = async (sub) => {
    if (!confirm(`Charge ${formatCurrency(sub.amount)} now for "${sub.name}"?`)) return;
    setProcessing(true);

    try {
      const { data: method } = await supabase.from('payment_methods').select('*').eq('id', sub.payment_method_id).single();
      const { data: customer } = await supabase.from('customer_payment_profiles').select('*').eq('id', sub.customer_profile_id).single();

      const result = await authorizeNetRecurring.chargeCustomerProfile(
        customer.authorize_customer_profile_id,
        method.authorize_payment_profile_id,
        parseFloat(sub.amount),
        sub.name
      );

      await supabase.from('payment_history').insert({
        customer_profile_id: sub.customer_profile_id,
        subscription_id: sub.id,
        payment_method_id: sub.payment_method_id,
        authorize_transaction_id: result.transactionId,
        transaction_type: 'subscription',
        amount: sub.amount,
        status: result.success ? 'success' : 'failed',
        card_type: method.card_type,
        card_last_four: method.card_last_four,
        description: sub.name,
        error_message: result.error
      });

      if (result.success) {
        setMessage({ type: 'success', text: `Payment of ${formatCurrency(sub.amount)} successful!` });
      } else {
        setMessage({ type: 'error', text: result.error || 'Payment failed' });
      }
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error processing payment' });
    } finally {
      setProcessing(false);
    }
  };

  const resetCustomerForm = () => {
    setCustomerForm({ name: '', email: '', phone: '', cardNumber: '', expirationDate: '', cvv: '', address: '', city: '', state: '', zip: '' });
  };

  const resetSubscriptionForm = () => {
    setSubscriptionForm({ customerId: '', name: '', amount: '', startDate: '', intervalLength: '1', intervalUnit: 'months', totalPayments: '', description: '' });
  };

  const getCardType = (num) => {
    const c = num.replace(/\D/g, '');
    if (/^4/.test(c)) return 'visa';
    if (/^5[1-5]/.test(c)) return 'mastercard';
    if (/^3[47]/.test(c)) return 'amex';
    return 'card';
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amt || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : 'N/A';

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      success: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      canceled: 'bg-red-100 text-red-800',
      failed: 'bg-red-100 text-red-800'
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>{status}</span>;
  };

  const filteredCustomers = customers.filter(c => 
    c.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.customer_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSubscriptions = subscriptions.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.customer_payment_profiles?.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    totalCustomers: customers.length,
    activeSubscriptions: subscriptions.filter(s => s.status === 'active').length,
    monthlyRevenue: subscriptions.filter(s => s.status === 'active').reduce((sum, s) => sum + parseFloat(s.amount || 0), 0),
    totalCollected: payments.filter(p => p.status === 'success').reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recurring Payments</h1>
          <p className="text-gray-500">Manage customer subscriptions and automated billing</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowCreateCustomer(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700">
            <FiUser /> Add Customer
          </button>
          <button onClick={() => setShowCreateSubscription(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700">
            <FiPlus /> New Subscription
          </button>
        </div>
      </div>

      <AnimatePresence>
        {message.text && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {message.type === 'error' ? <FiAlertCircle /> : <FiCheck />}{message.text}
            <button onClick={() => setMessage({ type: '', text: '' })} className="ml-auto"><FiX /></button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><FiUser className="w-5 h-5 text-blue-600" /></div>
            <div><p className="text-sm text-gray-500">Customers</p><p className="text-xl font-bold">{stats.totalCustomers}</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><FiCalendar className="w-5 h-5 text-green-600" /></div>
            <div><p className="text-sm text-gray-500">Active Subscriptions</p><p className="text-xl font-bold">{stats.activeSubscriptions}</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><FiDollarSign className="w-5 h-5 text-purple-600" /></div>
            <div><p className="text-sm text-gray-500">Monthly Revenue</p><p className="text-xl font-bold">{formatCurrency(stats.monthlyRevenue)}</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center"><FiCheck className="w-5 h-5 text-emerald-600" /></div>
            <div><p className="text-sm text-gray-500">Total Collected</p><p className="text-xl font-bold">{formatCurrency(stats.totalCollected)}</p></div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="border-b flex">
          {['subscriptions', 'customers', 'payments'].map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-6 py-4 text-sm font-medium capitalize ${activeTab === tab ? 'border-b-2 border-primary-600 text-primary-600' : 'text-gray-500'}`}>
              {tab}
            </button>
          ))}
          <div className="ml-auto p-2">
            <div className="relative">
              <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..." className="pl-10 pr-4 py-2 border rounded-lg text-sm w-64" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center"><FiRefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
        ) : (
          <>
            {activeTab === 'subscriptions' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Subscription</th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Next Payment</th>
                      <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredSubscriptions.length === 0 ? (
                      <tr><td colSpan="6" className="px-6 py-12 text-center text-gray-500">No subscriptions found</td></tr>
                    ) : filteredSubscriptions.map((sub) => (
                      <tr key={sub.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="font-medium">{sub.customer_payment_profiles?.customer_name}</p>
                          <p className="text-sm text-gray-500">{sub.customer_payment_profiles?.customer_email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium">{sub.name}</p>
                          <p className="text-sm text-gray-500">{sub.interval_length} {sub.interval_unit}</p>
                        </td>
                        <td className="px-6 py-4 text-right font-semibold">{formatCurrency(sub.amount)}</td>
                        <td className="px-6 py-4">{formatDate(sub.next_payment_date)}</td>
                        <td className="px-6 py-4 text-center">{getStatusBadge(sub.status)}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            {sub.status === 'active' && (
                              <>
                                <button onClick={() => handleChargeNow(sub)} disabled={processing}
                                  className="text-green-600 hover:text-green-800 p-1" title="Charge Now"><FiDollarSign /></button>
                                <button onClick={() => handleCancelSubscription(sub)} disabled={processing}
                                  className="text-red-600 hover:text-red-800 p-1" title="Cancel"><FiX /></button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'customers' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Profile ID</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredCustomers.length === 0 ? (
                      <tr><td colSpan="4" className="px-6 py-12 text-center text-gray-500">No customers found</td></tr>
                    ) : filteredCustomers.map((cust) => (
                      <tr key={cust.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">{cust.customer_name}</td>
                        <td className="px-6 py-4">
                          <p className="text-sm">{cust.customer_email}</p>
                          <p className="text-sm text-gray-500">{cust.customer_phone}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 font-mono">{cust.authorize_customer_profile_id}</td>
                        <td className="px-6 py-4 text-sm">{formatDate(cust.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'payments' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {payments.length === 0 ? (
                      <tr><td colSpan="5" className="px-6 py-12 text-center text-gray-500">No payments found</td></tr>
                    ) : payments.map((pay) => (
                      <tr key={pay.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm">{formatDate(pay.payment_date)}</td>
                        <td className="px-6 py-4">{pay.customer_payment_profiles?.customer_name}</td>
                        <td className="px-6 py-4 text-sm">{pay.description || pay.transaction_type}</td>
                        <td className="px-6 py-4 text-right font-medium">{formatCurrency(pay.amount)}</td>
                        <td className="px-6 py-4 text-center">{getStatusBadge(pay.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Customer Modal */}
      <AnimatePresence>
        {showCreateCustomer && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b flex justify-between items-center">
                <h2 className="text-xl font-bold">Add Customer</h2>
                <button onClick={() => { setShowCreateCustomer(false); resetCustomerForm(); }}><FiX className="w-6 h-6" /></button>
              </div>
              <form onSubmit={handleCreateCustomer} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Full Name *</label>
                    <input type="text" value={customerForm.name} onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email *</label>
                    <input type="email" value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <input type="tel" value={customerForm.phone} onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg" />
                  </div>
                </div>
                <hr className="my-4" />
                <p className="text-sm text-gray-500">Payment Method (optional - can add later)</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium mb-1">Card Number</label>
                    <input type="text" value={customerForm.cardNumber} onChange={(e) => setCustomerForm({ ...customerForm, cardNumber: e.target.value.replace(/\D/g, '').slice(0, 16) })}
                      className="w-full px-4 py-2 border rounded-lg" placeholder="Optional" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Expiration</label>
                    <input type="text" value={customerForm.expirationDate} onChange={(e) => setCustomerForm({ ...customerForm, expirationDate: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg" placeholder="MM/YY" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">CVV</label>
                    <input type="text" value={customerForm.cvv} onChange={(e) => setCustomerForm({ ...customerForm, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                      className="w-full px-4 py-2 border rounded-lg" placeholder="123" />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-sm font-medium mb-1">Billing Address</label>
                    <input type="text" value={customerForm.address} onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">City</label>
                    <input type="text" value={customerForm.city} onChange={(e) => setCustomerForm({ ...customerForm, city: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">State</label>
                    <input type="text" value={customerForm.state} onChange={(e) => setCustomerForm({ ...customerForm, state: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg" placeholder="TX" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">ZIP</label>
                    <input type="text" value={customerForm.zip} onChange={(e) => setCustomerForm({ ...customerForm, zip: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg" />
                  </div>
                </div>
                <button type="submit" disabled={processing} className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {processing ? <><FiRefreshCw className="animate-spin" /> Creating...</> : <><FiUser /> Create Customer</>}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Subscription Modal */}
      <AnimatePresence>
        {showCreateSubscription && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-white rounded-2xl max-w-lg w-full">
              <div className="p-6 border-b flex justify-between items-center">
                <h2 className="text-xl font-bold">Create Subscription</h2>
                <button onClick={() => { setShowCreateSubscription(false); resetSubscriptionForm(); }}><FiX className="w-6 h-6" /></button>
              </div>
              <form onSubmit={handleCreateSubscription} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Customer *</label>
                  <select value={subscriptionForm.customerId} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, customerId: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg" required>
                    <option value="">Select customer...</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.customer_name} ({c.customer_email})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subscription Name *</label>
                  <input type="text" value={subscriptionForm.name} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg" placeholder="e.g., 2024 Ford F-150 Monthly Payment" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Amount *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input type="number" step="0.01" min="1" value={subscriptionForm.amount} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, amount: e.target.value })}
                        className="w-full pl-8 pr-4 py-2 border rounded-lg" placeholder="0.00" required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Date *</label>
                    <input type="date" value={subscriptionForm.startDate} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, startDate: e.target.value })}
                      min={new Date().toISOString().split('T')[0]} className="w-full px-4 py-2 border rounded-lg" required />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Every</label>
                    <input type="number" min="1" value={subscriptionForm.intervalLength} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, intervalLength: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Period</label>
                    <select value={subscriptionForm.intervalUnit} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, intervalUnit: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg">
                      <option value="days">Days</option>
                      <option value="months">Months</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Total Payments</label>
                    <input type="number" min="1" value={subscriptionForm.totalPayments} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, totalPayments: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg" placeholder="∞" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <input type="text" value={subscriptionForm.description} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, description: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg" placeholder="Optional notes" />
                </div>
                <button type="submit" disabled={processing} className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {processing ? <><FiRefreshCw className="animate-spin" /> Creating...</> : <><FiPlus /> Create Subscription</>}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionManager;
